/**
 * The person record's lifecycle acts: one Manage menu, first in the record
 * (F1). Each item names its consequence and visits its exact Task address;
 * the section that owns a fact keeps its own work (edit, sign-in, role).
 */
import { useContext, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

import { BentoVisit } from "@/components/ui/box-visit-context";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { PersonManagementJob } from "./management-boxes";

export interface PersonManageChoice {
  job: PersonManagementJob;
  label: string;
  /** One line: what choosing it does. */
  does: string;
  destructive?: true;
}

export function PersonManageMenu({ personId, choices }: { personId: string; choices: PersonManageChoice[] }) {
  const host = useContext(BentoVisit);
  if (!host || !choices.length) return null;
  const from = `person-actions-${personId}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" aria-label="Manage person" data-person-manage="" className="pointer-coarse:min-h-11 pointer-coarse:px-4">
          Manage<ChevronDown aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 max-w-[calc(100vw-2rem)]">
        {choices.map(({ job, label, does, destructive }) => {
          const address = `person:${personId}/${job}` as const;
          const id = `${from}-${job}`;
          return (
            <DropdownMenuItem
              key={job}
              variant={destructive ? "destructive" : "default"}
              aria-labelledby={`${id}-label`}
              aria-describedby={`${id}-does`}
              data-act="visit"
              data-visit-to={address}
              className="flex-col items-start gap-0.5 py-2 pointer-coarse:min-h-11"
              onSelect={() => host.visit(address, { box: from })}
            >
              <span id={`${id}-label`} className="font-semibold">{label}</span>
              <span id={`${id}-does`} className="text-meta text-muted-foreground">{does}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Another lifecycle Task, offered from inside one: Archive instead, Offboard
 * instead, or Restore to People as an archived person's repair. From a
 * focused Task it replaces that Task, so Back still returns to the record.
 */
export function PersonVisitButton({ personId, job, from, label, instead = false }: { personId: string; job: PersonManagementJob; from: string; label: string; instead?: boolean }) {
  const host = useContext(BentoVisit);
  if (!host) return null;
  const address = `person:${personId}/${job}` as const;
  return (
    <Button type="button" variant="outline" size="sm" data-visit-to={address} className="pointer-coarse:min-h-11" onClick={() => host.visit(address, { box: from, next: instead || undefined })}>
      {label}
    </Button>
  );
}

/** Reading a person never changes a roster selection or enrollment. */
export function PersonRecordButton({ personId, name, from }: { personId?: string | null; name: string; from: string }) {
  const host = useContext(BentoVisit);
  if (!personId || !host) return null;
  const address = `person:${personId}` as const;
  return <Button type="button" variant="ghost" size="sm" className="shrink-0 pointer-coarse:min-h-11" aria-label={`Open student record for ${name}`} data-act="visit" data-visit-to={address} onClick={() => host.visit(address, { box: from })}>Student record</Button>;
}

/** One kind of effect in a lifecycle review (Teaching, Sign-in): a named list, each row its own effect. */
export function ReviewGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <p className="font-semibold text-foreground">{label}</p>
      <ul aria-label={label} className="grid gap-2">{children}</ul>
    </div>
  );
}
